import React, { useState, useEffect, useRef } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FaExchangeAlt } from 'react-icons/fa'
import { uploadManager } from '@renderer/utils/uploadManager'
import cogoToast from '@successtar/cogo-toast'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import { Button } from '@renderer/components'
import PhotoFrame from '@renderer/components/PhotoFrame'
import GallerySelectionModal from '../../components/strip/GallerySelectionModal'

interface UserContext {
  user?: {
    email: string
  }
}

interface Project {
  _id: string
  title: string
}

interface Frame {
  _id: string
  name: string
  thumbnail: string
  drawParams: {
    canvasWidth: number
    canvasHeight: number
    backgroundColor: string
    images: Array<{
      type: string
      top: number
      left: number
      width: number
      height: number
      src?: string
      rotation?: number
    }>
  }
}

// Print settings structure for remote printing
interface PrintSettings {
  orientation: 'landscape' | 'portrait'
  pageSize: { width: number; height: number }
  margins?: {
    marginType: 'default' | 'none' | 'printableArea' | 'custom'
    top?: number
    bottom?: number
    left?: number
    right?: number
  }
  dpi?: { horizontal: number; vertical: number }
  scaleFactor?: number
  printBackground?: boolean
  printResolutionScale?: number
}

const defaultPrintSettings: PrintSettings = {
  orientation: 'landscape',
  pageSize: { width: 156100, height: 105000 },
  margins: { marginType: 'default', top: 0, bottom: 0, left: 0, right: 0 },
  dpi: { horizontal: 600, vertical: 600 },
  scaleFactor: 100,
  printBackground: true,
  printResolutionScale: 1
}

const StripGenerator: React.FC = () => {
  const { t } = useTranslation()
  const context = useOutletContext<UserContext | null>()
  const [frames, setFrames] = useState<Frame[]>([])
  const [selectedFrame, setSelectedFrame] = useState<Frame | null>(null)
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])
  const [mirroredPhotos, setMirroredPhotos] = useState<Set<string>>(new Set())
  const [isAllMirrored, setIsAllMirrored] = useState(false)
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const [customId, setCustomId] = useState<string>('')
  const [uploadChecked, setUploadChecked] = useState(false)
  const [sessionIdValid, setSessionIdValid] = useState<null | boolean>(null)
  const [sessionIdChecking, setSessionIdChecking] = useState(false)
  const [remotePrinters, setRemotePrinters] = useState<
    {
      _id: string
      userId: string
      tunnelUrl: string
      projectId?: string
      createdAt: string
      printSettings?: PrintSettings
    }[]
  >([])
  const [selectedRemotePrinterUrl, setSelectedRemotePrinterUrl] = useState<string | null>(null)
  const [printSettings, setPrintSettings] = useState<PrintSettings>(defaultPrintSettings)
  const [isUploading, setIsUploading] = useState(false)
  const [step, setStep] = useState(1)
  const previewRef = useRef<PrintPreviewRef>(null)
  const isPortrait = window.innerHeight > window.innerWidth
  const [stripMode, setStripMode] = useState<'config' | 'upload'>('config')
  const [uploadedStripImage, setUploadedStripImage] = useState<string | null>(null)
  const [printChecked, setPrintChecked] = useState(false)
  const [currentMarginType, setCurrentMarginType] = useState(
    defaultPrintSettings.margins?.marginType || 'default'
  )
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false)
  const [galleryModalMode, setGalleryModalMode] = useState<'config' | 'upload'>('config')
  const [isDownloading, setIsDownloading] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)

  // Step 1: Fetch frames and projects
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        // Fetch frames
        const framesResponse = await fetch(`${process.env.VITE_API_URL}/api/frames`, {
          credentials: 'include',
          cache: 'no-cache'
        })
        const framesData = await framesResponse.json()
        setFrames(framesData)
        if (framesData.length > 0) {
          setSelectedFrame(framesData[0])
        }

        // Fetch projects
        const projectsResponse = await fetch(`${process.env.VITE_API_URL}/api/projects`, {
          credentials: 'include'
        })
        const projectsData = await projectsResponse.json()
        setProjects(projectsData)
        if (projectsData.length > 0) {
          setSelectedProjectId(projectsData[0]._id)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
        cogoToast.error(t('strip.errorFetchingData', 'Error fetching data'))
      }
    }
    fetchData()
  }, [t])

  // Step 3: Fetch remote printers
  useEffect(() => {
    const fetchRemotePrinters = async (): Promise<void> => {
      if (!context?.user?.email) return

      try {
        const response = await fetch(
          `${process.env.VITE_API_URL}/api/remote-printer?userId=${encodeURIComponent(context.user.email)}`,
          {
            credentials: 'include',
            cache: 'no-cache'
          }
        )

        if (response.ok) {
          const data = await response.json()
          if (data.printers) {
            setRemotePrinters(data.printers)
            setSelectedRemotePrinterUrl(data.printers[0]?.tunnelUrl || null) // Select the first one by default
            setPrintSettings(data?.printers[0]?.printSettings || defaultPrintSettings)
          } else {
            setRemotePrinters([])
            setSelectedRemotePrinterUrl(null)
          }
        } else {
          console.error('Failed to fetch remote printers:', response.statusText)
          cogoToast.error(t('strip.errorFetchingPrinters', 'Failed to fetch remote printers'))
        }
      } catch (error) {
        console.error('Error fetching remote printers:', error)
        cogoToast.error(t('strip.errorFetchingPrinters', 'Error fetching remote printers'))
      }
    }

    fetchRemotePrinters()
  }, [context?.user?.email])

  // Step 2: Validate session ID
  useEffect(() => {
    if (!uploadChecked || !customId.trim()) {
      setSessionIdValid(null)
      return
    }
    setSessionIdChecking(true)
    const timeout = setTimeout(async (): Promise<void> => {
      try {
        const response = await fetch(
          `${process.env.VITE_API_URL}/api/sessions?sessionId=${encodeURIComponent(customId)}`,
          { credentials: 'include' }
        )
        if (!response.ok) {
          setSessionIdValid(false)
        } else {
          const data = await response.json()
          setSessionIdValid(!!(data.sessions && data.sessions.length > 0))
        }
      } catch {
        setSessionIdValid(false)
      } finally {
        setSessionIdChecking(false)
      }
    }, 500)
    return (): void => clearTimeout(timeout)
  }, [uploadChecked, customId])

  // Step 1: Frame photo types
  const uniquePhotoTypes =
    selectedFrame?.drawParams.images
      .filter((img) => img.type.startsWith('photo-'))
      .map((img) => img.type)
      .filter((value, index, self) => self.indexOf(value) === index) || []
  const maxSelectablePhotos = uniquePhotoTypes.length

  // Step 1: Photo upload logic
  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const files = event.target.files
    if (!files) return

    const newPhotos: string[] = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const localUrl = URL.createObjectURL(file)
      newPhotos.push(localUrl)
    }

    setSelectedPhotos((prev) => {
      const updated = [...prev, ...newPhotos].slice(0, maxSelectablePhotos)
      return updated
    })
  }

  const handlePhotoClick = (photoUrl: string): void => {
    setSelectedPhotos((prev) => {
      let newSelection
      if (prev.includes(photoUrl)) {
        newSelection = prev.filter((url) => url !== photoUrl)
      } else if (prev.length < maxSelectablePhotos) {
        newSelection = [...prev, photoUrl]
      } else {
        newSelection = prev
      }
      return newSelection
    })
  }

  // Step 3: Remote printer error handling
  const handleRemotePrint = async (stripBlob: Blob): Promise<void> => {
    if (!selectedRemotePrinterUrl) {
      cogoToast.error(t('strip.noPrinterSelected', 'No printer selected.'))
      return
    }

    cogoToast.info(t('strip.printing', 'Printing...'))

    try {
      const reader = new FileReader()
      reader.readAsDataURL(stripBlob)
      reader.onloadend = async (): Promise<void> => {
        const base64data = reader.result as string
        const printResponse = await fetch(`${selectedRemotePrinterUrl}/print`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            requestId: Date.now().toString(),
            data: {
              images: [base64data],
              printerName: 'DS-RX1',
              pageSize: printSettings.pageSize,
              orientation: printSettings.orientation,
              margins: {
                marginType: currentMarginType,
                ...(currentMarginType === 'custom' ? printSettings.margins : {})
              },
              dpi: printSettings.dpi,
              scaleFactor: printSettings.scaleFactor,
              printBackground: printSettings.printBackground,
              printResolutionScale: printSettings.printResolutionScale
            }
          })
        })

        const printResult = await printResponse.json()
        if (printResponse.ok) {
          cogoToast.success(
            printResult.message || t('strip.printSuccess', 'Print job sent successfully!')
          )
        } else {
          cogoToast.error(
            t('strip.printFailed', `Print failed: ${printResult.error || 'Unknown error'}`)
          )
        }
      }
      reader.onerror = (): void => {
        console.error('Error converting blob to base64')
        cogoToast.error(t('strip.base64Error', 'Failed to prepare image for printing.'))
      }
    } catch (error) {
      console.error('Error sending print job:', error)
      cogoToast.error(t('strip.printError', 'Error sending print job.'))
    }
  }

  // Step 1: Mirror logic
  const handleMirrorAll = (): void => {
    const newMirroredPhotos = new Set(mirroredPhotos)
    const willMirrorAll = selectedPhotos.some((photo) => !mirroredPhotos.has(photo))
    selectedPhotos.forEach((photo) => {
      if (willMirrorAll) {
        newMirroredPhotos.add(photo)
      } else {
        newMirroredPhotos.delete(photo)
      }
    })
    setIsAllMirrored(willMirrorAll)
    setMirroredPhotos(newMirroredPhotos)
  }

  // Gallery modal handlers
  const handleOpenGalleryModal = (mode: 'config' | 'upload'): void => {
    setGalleryModalMode(mode)
    setIsGalleryModalOpen(true)
  }

  const handleGallerySelect = (photoUrls: string[]): void => {
    if (galleryModalMode === 'config') {
      // PhotoUrls from gallery modal are already blob URLs, safe to use directly
      setSelectedPhotos(photoUrls.slice(0, maxSelectablePhotos))
    } else {
      // For upload mode, use the first selected photo as the strip image
      // PhotoUrls from gallery modal are already blob URLs
      if (photoUrls.length > 0) {
        setUploadedStripImage(photoUrls[0])
      }
    }
  }

  // Step 1: Download strip immediately
  const handleDownloadStrip = async (): Promise<void> => {
    if (stripMode === 'config' && !selectedFrame) {
      cogoToast.error(t('strip.selectFrame', 'Please select a frame'))
      return
    }

    if (stripMode === 'upload' && !uploadedStripImage) {
      cogoToast.error(t('strip.uploadStripImage', 'Please upload a photo strip image'))
      return
    }

    setIsDownloading(true)
    try {
      let stripBlob: Blob | null | undefined = null

      if (stripMode === 'config') {
        stripBlob = await previewRef.current?.exportImage()
      } else if (stripMode === 'upload' && uploadedStripImage) {
        // If it's already a blob URL, convert it to blob directly
        if (uploadedStripImage.startsWith('blob:')) {
          const response = await fetch(uploadedStripImage)
          stripBlob = await response.blob()
        } else {
          // For CDN URLs, fetch without CORS mode to avoid issues
          const response = await fetch(uploadedStripImage)
          stripBlob = await response.blob()
        }
      }

      if (!stripBlob) throw new Error(t('strip.exportFailed', 'Failed to create strip preview'))

      // Download the strip
      const downloadLink = document.createElement('a')
      downloadLink.href = URL.createObjectURL(stripBlob)
      downloadLink.download = `dreambooth-${customId || 'strip'}-strip.jpg`
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      URL.revokeObjectURL(downloadLink.href)

      cogoToast.success(t('strip.downloading', 'Downloading...'))
      setStep(2)
    } catch (error) {
      console.error('Error downloading strip:', error)
      cogoToast.error(t('strip.generationError', 'Error generating photo strip'))
    } finally {
      setIsDownloading(false)
    }
  }

  // Step 3: Print settings change handler
  const handlePrintSettingChange = <K extends keyof PrintSettings>(
    key: K,
    value: PrintSettings[K]
  ): void => {
    setPrintSettings((prev) => ({ ...prev, [key]: value }))
  }
  const handleMarginTypeChange = (marginType: 'default' | 'none' | 'printableArea'): void => {
    setCurrentMarginType(marginType)
    setPrintSettings((prev) => ({
      ...prev,
      margins: {
        ...(prev.margins || defaultPrintSettings.margins),
        marginType: marginType
      }
    }))
  }

  // Step 2: Upload strip
  const handleUploadStrip = async (): Promise<void> => {
    if (!uploadChecked || !customId.trim()) {
      cogoToast.error(t('strip.enterSessionId', 'Enter Session ID (optional)'))
      return
    }

    if (!context?.user?.email) {
      cogoToast.error(t('strip.noUser', 'User not authenticated'))
      return
    }

    if (!sessionIdValid) {
      cogoToast.error(t('strip.sessionInvalid', 'Session ID invalid or not authorized'))
      return
    }

    setIsUploading(true)

    try {
      let stripBlob: Blob | null | undefined = null

      if (stripMode === 'config') {
        stripBlob = await previewRef.current?.exportImage()
      } else if (stripMode === 'upload' && uploadedStripImage) {
        // If it's already a blob URL, convert it to blob directly
        if (uploadedStripImage.startsWith('blob:')) {
          const response = await fetch(uploadedStripImage)
          stripBlob = await response.blob()
        } else {
          // For CDN URLs, fetch without CORS mode to avoid issues
          const response = await fetch(uploadedStripImage)
          stripBlob = await response.blob()
        }
      }

      if (!stripBlob) throw new Error(t('strip.exportFailed', 'Failed to create strip preview'))

      const stripFile = new File([stripBlob], `dreambooth-${customId || 'strip'}-strip.jpg`, {
        type: 'image/jpeg'
      })

      // Validate session before upload
      const response = await fetch(
        `${process.env.VITE_API_URL}/api/sessions?sessionId=${encodeURIComponent(customId)}`,
        { credentials: 'include' }
      )
      if (!response.ok) throw new Error('Failed to verify session ownership')
      const data = await response.json()
      if (!data.sessions || data.sessions.length === 0) {
        cogoToast.error(t('strip.invalidSessionId', 'Not authorized to upload to this gallery'))
        return
      }

      // Generate gallery ID using timestamp and custom ID
      const galleryId = `${Date.now()}-${customId}`
      const uploadPromise = uploadManager.addTask(
        stripFile,
        context.user.email,
        selectedProjectId,
        customId,
        galleryId,
        'strip'
      )
      const timeoutPromise = new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), 10000)
      })
      const result = await Promise.race([uploadPromise, timeoutPromise])
      if (result === null) {
        cogoToast.warn(
          t('strip.uploadTimeout', 'Upload is taking longer than expected. Continuing...')
        )
      } else {
        const uploadResponse = await result.promise
        if (uploadResponse?.url) {
          cogoToast.success(t('strip.uploadSuccess', 'Strip uploaded successfully'))
          setStep(3)
        }
      }
    } catch (error) {
      console.error('Error uploading strip:', error)
      cogoToast.error(t('strip.uploadFailed', 'Failed to upload strip - Invalid Session ID'))
    } finally {
      setIsUploading(false)
    }
  }

  // Step 3: Print strip
  const handlePrintStrip = async (): Promise<void> => {
    if (!printChecked || !selectedRemotePrinterUrl) {
      cogoToast.error(t('strip.noPrinterSelected', 'No printer selected.'))
      return
    }

    setIsPrinting(true)

    try {
      let stripBlob: Blob | null | undefined = null

      if (stripMode === 'config') {
        stripBlob = await previewRef.current?.exportImage()
      } else if (stripMode === 'upload' && uploadedStripImage) {
        // If it's already a blob URL, convert it to blob directly
        if (uploadedStripImage.startsWith('blob:')) {
          const response = await fetch(uploadedStripImage)
          stripBlob = await response.blob()
        } else {
          // For CDN URLs, fetch without CORS mode to avoid issues
          const response = await fetch(uploadedStripImage)
          stripBlob = await response.blob()
        }
      }

      if (!stripBlob) throw new Error(t('strip.exportFailed', 'Failed to create strip preview'))

      await handleRemotePrint(stripBlob)
      // Reset states after printing
      setTimeout(() => {
        resetStates()
      }, 2000)
    } catch (error) {
      console.error('Error printing strip:', error)
      cogoToast.error(t('strip.printError', 'Error sending print job.'))
    } finally {
      setIsPrinting(false)
    }
  }

  // Reset all states to initial values
  const resetStates = (): void => {
    setStep(1)
    // Reset to first frame if available, otherwise keep current or null
    if (frames.length > 0) {
      setSelectedFrame(frames[0])
    } else {
      setSelectedFrame(null)
    }
    setSelectedPhotos([])
    setMirroredPhotos(new Set())
    setIsAllMirrored(false)
    setCustomId('')
    setUploadChecked(false)
    setSessionIdValid(null)
    setPrintChecked(false)
    setUploadedStripImage(null)
    setStripMode('config')
    setIsDownloading(false)
    setIsUploading(false)
    setIsPrinting(false)
    setCurrentMarginType(defaultPrintSettings.margins?.marginType || 'default')
    // Reset printer settings
    if (remotePrinters.length > 0) {
      const firstPrinterSettings = remotePrinters[0]?.printSettings || defaultPrintSettings
      setPrintSettings(firstPrinterSettings)
      setSelectedRemotePrinterUrl(remotePrinters[0]?.tunnelUrl || null)
    } else {
      setPrintSettings(defaultPrintSettings)
      setSelectedRemotePrinterUrl(null)
    }
  }

  // Step 3: Done (reset states)
  const handleDone = (): void => {
    resetStates()
  }

  // Stepper logic
  const canGoToStep2 =
    (stripMode === 'config' && selectedFrame !== null) ||
    (stripMode === 'upload' && uploadedStripImage !== null)
  const canGoToStep3 = !uploadChecked || (uploadChecked && (!customId.trim() || sessionIdValid))

  return (
    <div className="w-full h-screen flex flex-col md:flex-row bg-whiteout overflow-hidden">
      {/* Preview - Top on mobile, Right on desktop */}
      <div className="w-full md:w-1/3 flex flex-col h-[50vh] md:h-screen p-4 md:p-8 bg-whiteout order-1 md:order-2">
        <div className="w-full mb-2 md:mb-4 flex-shrink-0">
          <h1 className="text-2xl md:text-4xl text-center text-carbon font-bold mb-1 md:mb-2">
            {t('strip.preview', 'Preview')}
          </h1>
          <p className="text-center text-carbon/60 text-xs md:text-base">
            {t('strip.previewHint', 'Preview of your photo strip')}
          </p>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center w-full px-4 min-h-0 overflow-hidden">
          {stripMode === 'config' && selectedFrame && (
            <div className="w-full h-full flex items-center justify-center max-w-[350px]">
              <PrintPreview
                ref={previewRef}
                frameConfig={{
                  id: selectedFrame._id,
                  baseImage: selectedFrame.thumbnail,
                  drawParams: selectedFrame.drawParams
                }}
                selectedPhotos={selectedPhotos.map((photo) =>
                  mirroredPhotos.has(photo) ? `mirror:${photo}` : photo
                )}
                className="w-full h-full"
                sessionId={customId}
              />
            </div>
          )}
          {stripMode === 'upload' && uploadedStripImage && (
            <div className="w-full h-full flex items-center justify-center max-w-[350px]">
              <img
                src={uploadedStripImage}
                alt="Uploaded Photo Strip"
                className="w-full h-full object-contain"
              />
            </div>
          )}
        </div>
      </div>
      {/* Steps - Bottom on mobile, Left on desktop */}
      <div className="flex flex-col w-full md:w-2/3 h-[50vh] md:h-full overflow-y-auto bg-white border-t md:border-t-0 md:border-r border-gray-200 shadow-lg px-4 md:px-8 py-6 md:py-10 order-2 md:order-1">
        {/* Stepper */}
        <div className="mb-10">
          <div className="flex flex-col gap-6">
            {/* Step 1 */}
            <div className={`flex items-start gap-4 ${step === 1 ? 'opacity-100' : 'opacity-60'}`}>
              <div
                className={`rounded-full w-8 h-8 flex items-center justify-center font-bold text-lg ${step === 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}
              >
                1
              </div>
              <div className="w-full">
                <div className="font-semibold text-lg">
                  {t('strip.step1Title', 'Step 1: Select Strip')}
                </div>
                <div className="text-gray-500 text-sm mb-2">
                  {t('strip.step1Desc', 'Choose a frame and upload photos if required.')}
                </div>
                <div className="flex gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="stripMode"
                      value="config"
                      checked={stripMode === 'config'}
                      onChange={() => {
                        setStripMode('config')
                        setUploadedStripImage(null)
                      }}
                      className="form-radio text-blue-500"
                      disabled={step !== 1}
                    />
                    <span className="text-sm">{t('strip.modeConfig', 'Choose from config')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="stripMode"
                      value="upload"
                      checked={stripMode === 'upload'}
                      onChange={() => {
                        setStripMode('upload')
                        setSelectedFrame(null)
                        setSelectedPhotos([])
                        setMirroredPhotos(new Set())
                      }}
                      className="form-radio text-blue-500"
                      disabled={step !== 1}
                    />
                    <span className="text-sm">
                      {t('strip.useExistingStrip', 'Use existing photo strip')}
                    </span>
                  </label>
                </div>

                {stripMode === 'config' && (
                  <div className="flex flex-col gap-2">
                    <select
                      title={t('strip.selectFrameTitle', 'Select Photo Strip Frame')}
                      value={selectedFrame?._id || ''}
                      onChange={(e) => {
                        const frame = frames.find((f) => f._id === e.target.value)
                        setSelectedFrame(frame || null)
                        setSelectedPhotos([])
                        setMirroredPhotos(new Set())
                      }}
                      className="px-4 py-2 rounded-lg border border-carbon mb-2"
                      disabled={step !== 1}
                    >
                      {frames.map((frame) => (
                        <option key={frame._id} value={frame._id}>
                          {frame.name}
                        </option>
                      ))}
                    </select>
                    {uniquePhotoTypes.length > 0 && (
                      <>
                        <div className="flex gap-2 mb-2">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handlePhotoUpload}
                            className="hidden"
                            id="photo-upload"
                            disabled={step !== 1}
                          />
                          <label
                            htmlFor="photo-upload"
                            className="flex-1 block p-4 border-2 border-dashed border-carbon/20 rounded-xl text-center cursor-pointer hover:border-carbon/40 transition-colors"
                          >
                            <span className="text-carbon/60">
                              {t('strip.uploadPhotos', 'Click to upload photos')}
                            </span>
                            <span className="block text-xs text-carbon/40 mt-1">
                              {t('strip.uploadHint', 'Select up to {{count}} photos', {
                                count: maxSelectablePhotos
                              })}
                            </span>
                          </label>
                          <button
                            type="button"
                            onClick={() => handleOpenGalleryModal('config')}
                            disabled={step !== 1}
                            className="flex-1 p-4 border-2 border-dashed border-carbon/20 rounded-xl text-center cursor-pointer hover:border-carbon/40 transition-colors bg-white"
                          >
                            <span className="text-carbon/60">
                              {t('strip.chooseFromGallery', 'Choose from gallery')}
                            </span>
                            <span className="block text-xs text-carbon/40 mt-1">
                              {t('strip.uploadHint', 'Select up to {{count}} photos', {
                                count: maxSelectablePhotos
                              })}
                            </span>
                          </button>
                        </div>
                        <div
                          className={`grid ${isPortrait ? 'grid-cols-2' : 'grid-cols-3'} gap-4 mb-2`}
                        >
                          {selectedPhotos.map((photoUrl, index) => {
                            const isMirrored = mirroredPhotos.has(photoUrl)
                            return (
                              <div key={index} className="relative group">
                                <button
                                  type="button"
                                  onClick={() => handlePhotoClick(photoUrl)}
                                  className="group relative overflow-hidden rounded-xl transition-all duration-300 w-full hover:scale-[0.98]"
                                  disabled={step !== 1}
                                >
                                  <PhotoFrame
                                    src={photoUrl}
                                    alt={`Photo ${index + 1}`}
                                    displayRatio={1}
                                    isMirrored={isMirrored}
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity duration-300 opacity-0 group-hover:opacity-100">
                                    <div className="text-center text-white">
                                      <div className="text-4xl mb-2">×</div>
                                      <p className="text-lg font-medium">
                                        {t('strip.remove', 'Click to Remove')}
                                      </p>
                                    </div>
                                  </div>
                                  <div
                                    className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
                                    style={{ backgroundColor: '#3B82F6' }}
                                  >
                                    <span
                                      style={{ color: '#FFFFFF' }}
                                      className="text-lg font-bold"
                                    >
                                      {index + 1}
                                    </span>
                                  </div>
                                </button>
                              </div>
                            )
                          })}
                        </div>
                        <button
                          type="button"
                          onClick={handleMirrorAll}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors shadow-md w-32 text-center ${
                            isAllMirrored
                              ? 'text-white bg-blue-500 hover:bg-blue-600'
                              : 'bg-white hover:bg-gray-100 text-carbon'
                          }`}
                          disabled={step !== 1}
                        >
                          <FaExchangeAlt
                            className={`text-lg ${isAllMirrored ? 'text-white' : 'text-carbon'}`}
                          />
                          <span
                            className={isAllMirrored ? 'text-sm text-white' : 'text-sm text-carbon'}
                          >
                            {t('strip.mirror', 'Mirror')}
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                )}

                {stripMode === 'upload' && (
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2 mb-2">
                      <input
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            setUploadedStripImage(URL.createObjectURL(file))
                          } else {
                            setUploadedStripImage(null)
                          }
                        }}
                        className="hidden"
                        id="strip-upload"
                        disabled={step !== 1}
                      />
                      <label
                        htmlFor="strip-upload"
                        className="flex-1 block p-4 border-2 border-dashed border-carbon/20 rounded-xl text-center cursor-pointer hover:border-carbon/40 transition-colors"
                      >
                        <span className="text-carbon/60">
                          {t('strip.uploadStripImage', 'Click to upload photo strip image')}
                        </span>
                        {uploadedStripImage && (
                          <span className="block text-xs text-carbon/40 mt-1">
                            {t('strip.uploaded', 'Uploaded:')} {uploadedStripImage.substring(0, 50)}
                            ...
                          </span>
                        )}
                      </label>
                      <button
                        type="button"
                        onClick={() => handleOpenGalleryModal('upload')}
                        disabled={step !== 1}
                        className="flex-1 p-4 border-2 border-dashed border-carbon/20 rounded-xl text-center cursor-pointer hover:border-carbon/40 transition-colors bg-white"
                      >
                        <span className="text-carbon/60">
                          {t('strip.chooseFromGallery', 'Choose from gallery')}
                        </span>
                      </button>
                    </div>
                    {uploadedStripImage && (
                      <div className="relative group w-fit mx-auto">
                        <PhotoFrame
                          src={uploadedStripImage}
                          alt="Uploaded Strip"
                          displayRatio={1}
                        />
                        <button
                          type="button"
                          onClick={() => setUploadedStripImage(null)}
                          className="absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity duration-300 opacity-0 group-hover:opacity-100"
                          disabled={step !== 1}
                        >
                          <div className="text-center text-white">
                            <div className="text-4xl mb-2">×</div>
                            <p className="text-lg font-medium">
                              {t('strip.remove', 'Click to Remove')}
                            </p>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <Button
                    text={
                      isDownloading
                        ? t('strip.downloading', 'Downloading...')
                        : t('common.continue', 'Continue')
                    }
                    onClick={handleDownloadStrip}
                    disabled={!canGoToStep2 || step !== 1 || isDownloading}
                    variant={canGoToStep2 ? 'primary' : 'secondary'}
                    className="py-1 px-3 text-sm"
                  />
                </div>
              </div>
            </div>
            {/* Step 2 */}
            <div
              className={`flex items-start gap-4 ${step === 2 ? 'opacity-100' : 'opacity-60'} ${!canGoToStep2 ? 'pointer-events-none' : ''}`}
            >
              <div
                className={`rounded-full w-8 h-8 flex items-center justify-center font-bold text-lg ${step === 2 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}
              >
                2
              </div>
              <div className="w-full">
                <div className="font-semibold text-lg">
                  {t('strip.step2Title', 'Step 2: Upload Configuration')}
                </div>
                <div className="text-gray-500 text-sm mb-2">
                  {t(
                    'strip.step2Desc',
                    'Choose whether to upload to gallery and configure project/session.'
                  )}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="upload-checkbox"
                    checked={uploadChecked}
                    onChange={(e) => setUploadChecked(e.target.checked)}
                    disabled={step !== 2}
                  />
                  <label htmlFor="upload-checkbox" className="text-sm">
                    {t('strip.uploadToGallery', 'Upload to gallery')}
                  </label>
                </div>
                {uploadChecked && (
                  <div className="flex flex-col gap-2 mb-2">
                    <select
                      title={t('strip.selectProjectTitle', 'Select Project')}
                      value={selectedProjectId}
                      onChange={(e) => setSelectedProjectId(e.target.value)}
                      className="px-4 py-2 rounded-lg border border-carbon"
                      disabled={step !== 2}
                    >
                      {projects.map((project) => (
                        <option key={project._id} value={project._id}>
                          {project.title}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={customId}
                      onChange={(e) => setCustomId(e.target.value)}
                      placeholder={t('strip.enterSessionId', 'Enter Session ID (optional)')}
                      className="px-4 py-2 rounded-lg border border-carbon"
                      disabled={step !== 2}
                    />
                    {customId.trim() && (
                      <div className="text-xs mt-1">
                        {sessionIdChecking ? (
                          <span className="text-blue-500">
                            {t('strip.validatingSession', 'Validating session...')}
                          </span>
                        ) : sessionIdValid === true ? (
                          <span className="text-green-600">
                            {t('strip.sessionValid', 'Session ID valid')}
                          </span>
                        ) : sessionIdValid === false ? (
                          <span className="text-red-600">
                            {t('strip.sessionInvalid', 'Session ID invalid or not authorized')}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-4 flex justify-end">
                  <Button
                    text={t('common.back', 'Back')}
                    onClick={() => setStep(1)}
                    variant="secondary"
                    className="mr-2 py-1 px-3 text-sm"
                    disabled={step !== 2}
                  />
                  {uploadChecked ? (
                    <Button
                      text={
                        isUploading
                          ? t('strip.uploading', 'Uploading...')
                          : t('strip.step2.upload', 'Upload')
                      }
                      onClick={handleUploadStrip}
                      disabled={!canGoToStep3 || step !== 2 || isUploading}
                      variant={canGoToStep3 ? 'primary' : 'secondary'}
                      className="py-1 px-3 text-sm"
                    />
                  ) : (
                    <Button
                      text={t('strip.step2.skip', 'Skip')}
                      onClick={() => setStep(3)}
                      disabled={step !== 2}
                      variant="primary"
                      className="py-1 px-3 text-sm"
                    />
                  )}
                </div>
              </div>
            </div>
            {/* Step 3 */}
            <div
              className={`flex items-start gap-4 ${step === 3 ? 'opacity-100' : 'opacity-60'} ${!canGoToStep3 ? 'pointer-events-none' : ''}`}
            >
              <div
                className={`rounded-full w-8 h-8 flex items-center justify-center font-bold text-lg ${step === 3 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}
              >
                3
              </div>
              <div className="w-full">
                <div className="font-semibold text-lg">
                  {t('strip.step3Title', 'Step 3: Remote Printing Configuration')}
                </div>
                <div className="text-gray-500 text-sm mb-2">
                  {t('strip.step3Desc', 'Select a remote printer and configure print settings.')}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="print-checkbox"
                    checked={printChecked}
                    onChange={(e) => setPrintChecked(e.target.checked)}
                    disabled={step !== 3}
                  />
                  <label htmlFor="print-checkbox" className="text-sm">
                    {t('strip.printStrip', 'Print strip')}
                  </label>
                </div>
                {printChecked && (
                  <div className="flex flex-col gap-2 mb-2">
                    <select
                      title={t('strip.selectRemotePrinterTitle', 'Select Remote Printer')}
                      value={selectedRemotePrinterUrl || ''}
                      onChange={(e) => setSelectedRemotePrinterUrl(e.target.value)}
                      className="px-4 py-2 rounded-lg border border-carbon"
                      disabled={step !== 3}
                    >
                      <option value="">{t('strip.noPrinterSelected', 'No Remote Printer')}</option>
                      {remotePrinters.map((printer) => {
                        const project = projects.find((p) => p._id === printer.projectId)
                        const printerName = printer.tunnelUrl
                          .replace('https://', '')
                          .replace('.trycloudflare.com', '')
                        return (
                          <option key={printer._id} value={printer.tunnelUrl}>
                            {project ? `${project.title} - ${printerName}` : printerName}
                          </option>
                        )
                      })}
                    </select>
                    {selectedRemotePrinterUrl && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            {t('printerSettings.orientation', 'Orientation')}
                          </label>
                          <select
                            title={t('printerSettings.orientation', 'Orientation')}
                            value={printSettings.orientation}
                            onChange={(e) =>
                              handlePrintSettingChange(
                                'orientation',
                                e.target.value as 'landscape' | 'portrait'
                              )
                            }
                            className="w-full p-2 border border-gray-300 rounded-md"
                            disabled={step !== 3}
                          >
                            <option value="landscape">
                              {t('printerSettings.landscape', 'Landscape')}
                            </option>
                            <option value="portrait">
                              {t('printerSettings.portrait', 'Portrait')}
                            </option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            {t('printerSettings.pageSize', 'Page Size (microns)')}
                          </label>
                          <div className="flex gap-1">
                            <input
                              type="number"
                              min={1}
                              value={printSettings.pageSize.width}
                              onChange={(e) =>
                                handlePrintSettingChange('pageSize', {
                                  ...printSettings.pageSize,
                                  width: parseInt(e.target.value)
                                })
                              }
                              className="w-1/2 p-2 border border-gray-300 rounded-md"
                              placeholder={t('printerSettings.width', 'Width')}
                              disabled={step !== 3}
                            />
                            <input
                              type="number"
                              min={1}
                              value={printSettings.pageSize.height}
                              onChange={(e) =>
                                handlePrintSettingChange('pageSize', {
                                  ...printSettings.pageSize,
                                  height: parseInt(e.target.value)
                                })
                              }
                              className="w-1/2 p-2 border border-gray-300 rounded-md"
                              placeholder={t('printerSettings.height', 'Height')}
                              disabled={step !== 3}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            {t('printerSettings.margins', 'Margins')}
                          </label>
                          <select
                            title={t('printerSettings.margins', 'Margins Type')}
                            value={currentMarginType}
                            onChange={(e) =>
                              handleMarginTypeChange(
                                e.target.value as 'default' | 'none' | 'printableArea'
                              )
                            }
                            className="w-full p-2 border border-gray-300 rounded-md mb-1"
                            disabled={step !== 3}
                          >
                            <option value="default">
                              {t('printerSettings.margins.default', 'Default')}
                            </option>
                            <option value="none">
                              {t('printerSettings.margins.none', 'None')}
                            </option>
                            <option value="printableArea">
                              {t('printerSettings.margins.printableArea', 'Printable Area')}
                            </option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            {t('printerSettings.dpi', 'DPI')}
                          </label>
                          <div className="flex gap-1">
                            <input
                              type="number"
                              value={printSettings.dpi?.horizontal || 600}
                              onChange={(e) =>
                                handlePrintSettingChange('dpi', {
                                  ...(printSettings.dpi || { horizontal: 600, vertical: 600 }),
                                  horizontal: parseInt(e.target.value)
                                })
                              }
                              className="w-1/2 p-2 border border-gray-300 rounded-md"
                              placeholder={t('printerSettings.dpi.horizontal', 'Horizontal')}
                              disabled={step !== 3}
                            />
                            <input
                              type="number"
                              value={printSettings.dpi?.vertical || 600}
                              onChange={(e) =>
                                handlePrintSettingChange('dpi', {
                                  ...(printSettings.dpi || { horizontal: 600, vertical: 600 }),
                                  vertical: parseInt(e.target.value)
                                })
                              }
                              className="w-1/2 p-2 border border-gray-300 rounded-md"
                              placeholder={t('printerSettings.dpi.vertical', 'Vertical')}
                              disabled={step !== 3}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            {t('settings.printer.scaleFactor', 'Scale Factor')}
                          </label>
                          <input
                            aria-label={t('settings.printer.scaleFactor', 'Scale Factor')}
                            type="number"
                            min={10}
                            max={200}
                            value={printSettings.scaleFactor || 100}
                            onChange={(e) =>
                              handlePrintSettingChange(
                                'scaleFactor',
                                Math.max(10, Math.min(200, Number(e.target.value)))
                              )
                            }
                            className="w-full p-2 border border-gray-300 rounded-md"
                            disabled={step !== 3}
                          />
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="checkbox"
                            id="printBackground"
                            checked={printSettings.printBackground ?? true}
                            onChange={(e) =>
                              handlePrintSettingChange('printBackground', e.target.checked)
                            }
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            disabled={step !== 3}
                          />
                          <label htmlFor="printBackground" className="text-xs text-gray-700">
                            {t('settings.printer.printBackground', 'Print Background')}
                          </label>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            {t(
                              'printerSettings.printResolutionScale.title',
                              'Print Resolution Scale'
                            )}
                          </label>
                          <select
                            title={t(
                              'printerSettings.printResolutionScale.title',
                              'Print Resolution Scale'
                            )}
                            value={printSettings.printResolutionScale || 1}
                            onChange={(e) =>
                              handlePrintSettingChange(
                                'printResolutionScale',
                                parseFloat(e.target.value)
                              )
                            }
                            className="w-full p-2 border border-gray-300 rounded-md"
                            disabled={step !== 3}
                          >
                            <option value={0.5}>0.5x</option>
                            <option value={1}>1x (Default)</option>
                            <option value={2}>2x</option>
                            <option value={3}>3x</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-4 flex justify-end">
                  <Button
                    text={t('common.back', 'Back')}
                    onClick={() => setStep(2)}
                    variant="secondary"
                    className="mr-2 py-1 px-3 text-sm"
                    disabled={step !== 3}
                  />
                  {printChecked ? (
                    <Button
                      text={
                        isPrinting
                          ? t('strip.printing', 'Printing...')
                          : t('strip.step3.print', 'Print')
                      }
                      onClick={handlePrintStrip}
                      disabled={!selectedRemotePrinterUrl || step !== 3 || isPrinting}
                      variant={selectedRemotePrinterUrl ? 'primary' : 'secondary'}
                      className="py-1 px-3 text-sm"
                    />
                  ) : (
                    <Button
                      text={t('strip.step3.done', 'Done')}
                      onClick={handleDone}
                      disabled={step !== 3}
                      variant="primary"
                      className="py-1 px-3 text-sm"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Gallery Selection Modal */}
      <GallerySelectionModal
        isOpen={isGalleryModalOpen}
        onClose={() => setIsGalleryModalOpen(false)}
        onSelect={handleGallerySelect}
        maxSelectable={galleryModalMode === 'config' ? maxSelectablePhotos : 1}
        filterType={galleryModalMode === 'config' ? 'image' : 'strip'}
      />
    </div>
  )
}

export default StripGenerator
